import '@/styles/globals.css'
import '@/styles/react-big-calendar.css'
import Head from 'next/head'
import type { AppProps } from 'next/app'
import { RecoilRoot, RecoilEnv } from 'recoil'
import React, { useState, useEffect } from 'react'
import { Hydrate, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { DefaultSeo } from 'next-seo'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import dynamic from 'next/dynamic'
import { GoogleTagManager } from '@next/third-parties/google'
import { useRouter } from 'next/router'
// eslint-disable-next-line import/no-named-as-default
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import localFont from 'next/font/local'

import SEO from '../../nextseo.config'

import GlobalError from '@/components/GlobalError'
import NetworkErrorAlert from '@/components/NetworkErrorAlert'
import NotOnlineBanner from '@/components/NotOnlineBanner'
import { NetworkStatusProvider, useNetworkStatus } from '@/hooks/useNetworkStatus'
import ErrorNotification from '@/components/ErrorNotification'
import AmplitudeProvider from '@/components/Amplitude/AmplitudeProvider'
import AmplitudePageViewWithUserId from '@/components/Amplitude/AmplitudePageViewWithUserId'
import { DefaultModalContext } from '@/lib/utils'
import { ModalProps, ModalType } from '@/types'
import DefaultModal from '@/components/Shared/Modal/DefaultModal'
import AuthProviders from '@/components/Auth/AuthProviders'
import CustomToastContainer from '@/components/Shared/Toast/ToastContainer'
import { BackgroundMonitorProvider } from '@/components/BackgroundMonitor/BackgroundMonitorProvider'
import { useStaleChunkReload } from '@/hooks/useStaleChunkReload'
import { useThemeListeners } from '@/hooks/useTheme'

RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false

// Load Pretendard and Pretendard JP separately and expose as CSS variables
const pretendard = localFont({
  src: [
    {
      path: '../../public/fonts/PretendardVariable.ttf',
      weight: '45 920',
    },
  ],
  variable: '--font-pretendard',
})

const pretendardJP = localFont({
  src: [
    {
      path: '../../public/fonts/PretendardJPVariable.ttf',
      weight: '45 920',
    },
  ],
  variable: '--font-pretendard-jp',
})

/**
 * WDYR (why-did-you-render) helps locate unnecessary re-renders and fix them
 * Applied in development environment, on the frontend only
 *
 * @see https://github.com/welldone-software/why-did-you-render
 */
if (typeof window !== 'undefined') {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const whyDidYouRender = require('@welldone-software/why-did-you-render')

    whyDidYouRender(React, {
      trackAllPureComponents: true,
      trackHooks: true,
      logOwnerReasons: true,
      collapseGroups: true,
    })
  }
}

const AudioRecorder = dynamic(() => import('../components/Rms/Source/AudioRecorder'), {
  ssr: false,
})

// NProgress configuration
NProgress.configure({
  showSpinner: false,
  minimum: 0.3,
  easing: 'ease',
  speed: 800,
})

export default function App({ Component, pageProps }: AppProps) {
  const [isNetworkError, setIsNetworkError] = useState<boolean>(false)
  const router = useRouter()

  // NProgress setup for route changes
  useEffect(() => {
    const handleStart = () => {
      NProgress.start()
    }
    const handleStop = () => {
      NProgress.done()
    }
    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleStop)
    router.events.on('routeChangeError', handleStop)

    return () => {
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleStop)
      router.events.off('routeChangeError', handleStop)
    }
  }, [router])

  // Stale-build fallback: 배포 후 이전 buildId 의 dynamic import chunk 가 404 나면
  // guarded hard reload (route 전환 실패는 Next.js 라우터가 자체 hard navigation 으로 복구)
  useStaleChunkReload()

  // Reads the stored theme after hydration and keeps it in step with the OS setting and
  // with other tabs. The class itself is already on <html> from the _document bootstrap.
  useThemeListeners()

  const [defaultModalOpen, setDefaultModalOpen] = useState<boolean>(false)
  const [defaultModalProps, setDefaultModalProps] = useState<ModalProps>({
    title: 'modal',
    body: 'body',
    onSubmit: () => {},
    submitText: 'submit',
    type: ModalType.Submit,
  })

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error: unknown, query) => {
            if (query.state.data !== undefined && (error as Error).message === 'Network Error') {
              setIsNetworkError(true)
            }
          },
          onSuccess: () => {
            setIsNetworkError(false)
          },
        }),
        defaultOptions: {
          queries: {
            networkMode: 'online',
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
          },
          mutations: {
            networkMode: 'online',
          },
        },
      })
  )

  const openModal = (props: ModalProps) => {
    setDefaultModalProps(props)
    setDefaultModalOpen(true)
  }

  const closeModal = () => {
    setDefaultModalOpen(false)
  }

  const authProviders = [
    {
      type: 'google' as const,
      props: {
        clientId: process.env.RMS_GOOGLE_CLIENT_ID as string,
      },
    },
    // Other Providers
  ]

  // const useAudioRecorder = dynamic(() => import('../hooks/useAudioRecorder'), { ssr: false })

  return (
    <div className={`${pretendard.variable} ${pretendardJP.variable} font-root`}>
      <GlobalError>
        <RecoilRoot>
          <NetworkStatusProvider>
            <QueryClientProvider client={queryClient}>
              <Hydrate state={pageProps.dehydratedState}>
                <AmplitudeProvider>
                  <NetworkStatusConsumer>
                    <AuthProviders providers={authProviders}>
                      <AudioRecorder>
                        <BackgroundMonitorProvider>
                          <CustomToastContainer />
                          <DefaultSeo {...SEO} />
                          <Head>
                            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                          </Head>
                          {isNetworkError && <NetworkErrorAlert />}
                          <ErrorNotification />
                          <AmplitudePageViewWithUserId />
                          <DefaultModalContext.Provider value={{ openModal, closeModal }}>
                            <Component {...pageProps} />
                          </DefaultModalContext.Provider>
                          <DefaultModal
                            className="lg:!max-w-[600px]"
                            open={defaultModalOpen}
                            setOpen={setDefaultModalOpen}
                            modalProps={defaultModalProps}
                          />
                          {process.env.NODE_ENV === 'development' && (
                            <ReactQueryDevtools initialIsOpen={false} />
                          )}
                          <GoogleTagManager gtmId={process.env.GTM_ID as string} />
                        </BackgroundMonitorProvider>
                      </AudioRecorder>
                    </AuthProviders>
                  </NetworkStatusConsumer>
                </AmplitudeProvider>
              </Hydrate>
            </QueryClientProvider>
          </NetworkStatusProvider>
        </RecoilRoot>
      </GlobalError>
    </div>
  )
}

function NetworkStatusConsumer({ children }: { children: React.ReactNode }) {
  const isOnline = useNetworkStatus()

  return (
    <>
      {!isOnline && <NotOnlineBanner />}
      {children}
    </>
  )
}
